import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user, token } = useAuth();
  const [wishlistIds, setWishlistIds] = useState(new Set());

  // Load wishlist when user logs in
  useEffect(() => {
    if (user && token) {
      axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const ids = (res.data.products || []).map(p => p.id);
          setWishlistIds(new Set(ids));
        })
        .catch(() => setWishlistIds(new Set()));
    } else {
      setWishlistIds(new Set());
    }
  }, [user, token]);

  const isWishlisted = useCallback((productId) => wishlistIds.has(productId), [wishlistIds]);

  const toggleWishlist = useCallback(async (productId) => {
    if (!token) return { error: 'login_required' };
    try {
      const res = await axios.post(
        `${API}/wishlist/toggle`,
        { product_id: productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWishlistIds(prev => {
        const next = new Set(prev);
        if (res.data.action === 'added') next.add(productId);
        else next.delete(productId);
        return next;
      });
      return { action: res.data.action };
    } catch {
      return { error: 'failed' };
    }
  }, [token]);

  return (
    <WishlistContext.Provider value={{ wishlistIds, isWishlisted, toggleWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
